// src/hooks/notificationsContext.js
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import getServerOffset from "../utils/getServerOffset";
import { auth } from "../firebase";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const [messages, setMessages] = useState([]);

  const offsetRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const offset = await getServerOffset();
        if (mounted) offsetRef.current = offset || 0;
      } catch {

        offsetRef.current = 0;
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const now = () => Date.now() + offsetRef.current;

  const addNotification = useCallback((text, type = "info", timestamp) => {
    const ts =
      typeof timestamp === "number"
        ? timestamp
        : timestamp instanceof Date
        ? timestamp.getTime()
        : now();

    const id = `${ts}-${Math.random().toString(36).slice(2, 7)}`;

    setMessages((prev) => [...prev, { id, text, type, timestamp: ts }]);
  }, []);

  const removeNotification = useCallback((id) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const clearNotifications = useCallback(() => setMessages([]), []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user) {
        setMessages([]);
      }
    });
    return () => unsub();
  }, []);

  const addMessage = addNotification;
  const removeMessage = removeNotification;
  const clearAll = clearNotifications;

  return (
    <NotificationsContext.Provider
      value={{
        messages,
        addNotification,
        removeNotification,
        clearNotifications,
        addMessage,
        removeMessage,
        clearAll,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider"
    );
  }
  return ctx;
}

export { NotificationsContext };
